import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PDFDocument } from "pdf-lib";

const apiUrl = process.env.REACT_APP_API_URL;

function CreateEmail() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [isGenerated, setIsGenerated] = useState(false);
  const [emailId, setEmailId] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [loadingBind, setLoadingBind] = useState(false);
  const [loadingOutlook, setLoadingOutlook] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [error, setError] = useState(null);
  const [outlookClicked, setOutlookClicked] = useState(false);
  const [status, setStatus] = useState(null);
  const [existingEmail, setExistingEmail] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [carrierInfo, setCarrierInfo] = useState(null); // { isAdmitted, carrierName }
  const [checkingCarrier, setCheckingCarrier] = useState(false);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleBack = (e) => {
    e.preventDefault();
    setIsGenerated(false);
    setEmailContent("");
    setEmailId(null);
    navigate("/manage-emails");
  };

  const checkTemplateExistence = async (type) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${apiUrl}check-template-existence/${quoteId}?type=${type}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      console.log(data.data);

      if (response.ok && data.data.exists) {
        setStatus(data.data.status);
        setExistingEmail(data.data.emailId);
        setShowPrompt(true);
        return true;
      }

      return false;
    } catch (error) {
      setError("An error occurred while checking template existence");
      console.error("Error checking template existence:", error);
    }
    return false;
  };

  const checkCarrierType = async () => {
    if (!quoteId) return null;
    setCheckingCarrier(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${apiUrl}check-carrier-type/${quoteId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (response.ok) {
        setCarrierInfo(data.data);
        return data.data;
      } else {
        setError(data.message || "Failed to check carrier type");
        return null;
      }
    } catch (error) {
      setError("Error checking carrier type");
      console.error("Error checking carrier type:", error);
      return null;
    } finally {
      setCheckingCarrier(false);
    }
  };

  // Function to generate email content
  const generateEmailContent = async (type, skipCheck = false) => {
    setSelectedType(type);
    if (!skipCheck) {
      const templateExists = await checkTemplateExistence(type);
      if (templateExists) return;
    }

    // Auto-detect carrier type
    let carrier = carrierInfo;
    if (!carrier) {
      carrier = await checkCarrierType();
      if (!carrier) return; // Error already set
    }

    if (type === "quote") setLoadingQuote(true);
    else if (type === "bind") setLoadingBind(true);

    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${apiUrl}create-email-template/${quoteId}`,
        {
          method: "POST",
          body: JSON.stringify({
            type: type,
            ...(carrier && !carrier.isAdmitted ? { templateType: "es" } : {}),
          }),
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setName(data.data.emailName); // Set the name from the response
        setEmailContent(data.data.emailTemplate.trim());
        setEmailId(data.data._id);

        const { docBase64 } = data.pdfFiles;

        if (docBase64) {
          const docBlob = base64ToBlob(docBase64, "application/pdf");
          // Store the blob for later use when the button is clicked
          setPdfBlob(docBlob);
        } else {
          setError("Failed to load PDF files");
        }

        setIsGenerated(true);
      } else {
        // Set error message from the API response
        setError(data.message || "Failed to generate email content");
      }
    } catch (error) {
      setError("An unexpected error occurred while generating email content");
      console.error("Error generating email content:", error);
    } finally {
      if (type === "quote") setLoadingQuote(false);
      else if (type === "bind") setLoadingBind(false);
    }
  };

  // Button Click Event to Open PDF
  const handleOpenPDF = () => {
    if (!pdfBlob) {
      setError("PDF is not available.");
      return;
    }
    // Create a blob URL for the merged PDF
    const pdfUrl = URL.createObjectURL(pdfBlob);

    // Open the PDF in a new window/tab
    window.open(pdfUrl);
  };

  // Utility function to convert base64 to Blob (Browser-safe)
  const base64ToBlob = (base64, contentType = "") => {
    try {
      // Remove any line breaks or invalid characters from the base64 string
      const sanitizedBase64 = base64.replace(/\s/g, "");
      const binaryString = atob(sanitizedBase64); // Decode base64 string to binary string
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      // Convert binary string to a byte array
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes], { type: contentType });
    } catch (error) {
      console.error("Error converting base64 to Blob:", error);
      throw new Error("Invalid base64 string");
    }
  };

  const handleOpenInOutlook = async () => {
    if (emailId) {
      setLoadingOutlook(true);
      setOutlookClicked(true); // Disable Cancel button
      try {
        const token = localStorage.getItem("accessToken");
        const response = await fetch(
          `${apiUrl}create-outlook-draft/${emailId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.url) {
            window.open(data.url, "_blank");
          } else {
            console.error("No URL provided in the response");
          }
        } else {
          setError("Failed to create Outlook draft");
        }
      } catch (error) {
        setError("An unexpected error occurred while creating Outlook draft");
        console.error("Error creating Outlook draft:", error);
      } finally {
        setLoadingOutlook(false);
      }
    } else {
      setError("No email ID provided");
    }
  };

  const handleSaveAsDraft = () => navigate("/manage-emails");

  const handleCancel = async () => {
    if (emailId) {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await fetch(
          `${apiUrl}delete-email-template/${emailId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) setError("Failed to delete the email template");
      } catch (error) {
        setError(
          "An unexpected error occurred while deleting the email template"
        );
        console.error("Error deleting email template:", error);
      }
    }
    navigate("/manage-emails");
  };

  const handlePromptAction = async (action, type) => {
    if (action === "yes") {
      setShowPrompt(false);
      await generateEmailContent(type, true);
    } else if (action === "viewDraft") {
      navigate(`/view-email/${existingEmail}`);
    } else if (action === "viewSent") {
      navigate(`/view-email/${existingEmail}`);
    } else if (action === "viewInOutlook") {
      window.open("https://outlook.office.com/mail/drafts", "_blank");
      navigate(`/view-email/${existingEmail}`);
    }
  };

  return (
    <>
      <button onClick={handleBack} className="btn btn-outline-dark">
        <i className="bi bi-chevron-left"></i> Back
      </button>
      {error && (
        <div className="alert alert-danger mt-2" role="alert">
          {error}
        </div>
      )}
      <div className="dashboard-title my-3">
        <h1>Create Email</h1>
      </div>
      {showPrompt && (
        <div className="alert alert-warning mt-2" role="alert">
          This controlNo already has this template with status:{" "}
          <strong>{status}</strong>.
          <br />
          Do you want to create a new version?
          <div className="mt-2">
            <button
              className="btn btn-primary me-2"
              onClick={() => handlePromptAction("yes", selectedType)}
            >
              Yes
            </button>
            {status === "Draft" && (
              <button
                className="btn btn-primary"
                onClick={() => handlePromptAction("viewDraft")}
              >
                View Existing Draft
              </button>
            )}
            {status === "Sent" && (
              <button
                className="btn btn-primary me-2"
                onClick={() => handlePromptAction("viewSent")}
              >
                View Sent Email
              </button>
            )}
            {status === "In Outlook" && (
              <button
                className="btn btn-primary"
                onClick={() => handlePromptAction("viewInOutlook")}
              >
                View in Outlook
              </button>
            )}
          </div>
        </div>
      )}
      <div className="row align-items-end mb-3 clearfix">
        <div className="col-12 col-lg-6">
          <div className="v-space">
            <label htmlFor="netrateID" className="form-label">
              <b>Control No</b>
            </label>
            <input
              type="text"
              className="form-control"
              id="netrateID"
              placeholder="Enter Control No"
              value={quoteId}
              onChange={(e) => {
                setQuoteId(e.target.value);
                setCarrierInfo(null);
              }}
            />
          </div>
        </div>
        {carrierInfo && (
          <div className="col-12 col-lg-6 d-flex align-items-end">
            <span className={`badge ${carrierInfo.isAdmitted ? 'bg-primary' : 'bg-warning text-dark'} fs-6`}>
              {carrierInfo.isAdmitted ? 'Admitted' : 'E&S (Non-Admitted)'}
              {carrierInfo.carrierName && ` — ${carrierInfo.carrierName}`}
            </span>
          </div>
        )}
      </div>
      {name && (
        <div className="row mb-3 clearfix">
          <div className="col-12 col-lg-6">
            <div className="v-space">
              <label className="form-label">Name:</label>
              <p>
                <strong>{name}</strong>
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="d-flex gap-3">
        <button
          type="button"
          className="btn btn-primary mt-3"
          onClick={() => generateEmailContent("quote")}
          disabled={isGenerated || loadingQuote}
        >
          {loadingQuote ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            "Generate Quote Email Content"
          )}
        </button>
        <button
          type="button"
          className="btn btn-primary mt-3"
          onClick={() => generateEmailContent("bind")}
          disabled={isGenerated || loadingBind}
        >
          {loadingBind ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            "Generate Bind Email Content"
          )}
        </button>
      </div>
      <div className="mt-5 mb-3">
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ flexGrow: 1 }}></div>
          {pdfBlob && (
            <button
              onClick={handleOpenPDF}
              className="btn btn-primary"
              style={{ marginBottom: "5px" }}
            >
              View PDF
            </button>
          )}
        </div>
        <div
          className="form-control"
          id="textarea"
          style={{
            whiteSpace: "normal",
            minHeight: "200px",
            border: "1px solid #ccc",
            padding: "10px",
            overflowWrap: "break-word",
            flexGrow: 1,
          }}
          dangerouslySetInnerHTML={{ __html: emailContent }}
        />
      </div>
      <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-3">
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleOpenInOutlook}
          disabled={!isGenerated || loadingOutlook || outlookClicked}
        >
          {loadingOutlook ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            "Open in Outlook"
          )}
        </button>

        <button
          className="btn btn-success"
          type="button"
          onClick={handleSaveAsDraft}
          disabled={!isGenerated || outlookClicked}
        >
          Save in ATE Portal
        </button>
        <button
          className="btn btn-outline-dark"
          onClick={handleCancel}
          disabled={outlookClicked} // Disable Cancel button when Outlook is clicked
        >
          Cancel
        </button>
      </div>
    </>
  );
}

export default CreateEmail;
